<template>
  <div>
    <!-- Header -->
    <div
      class="rounded-t-xl border-b border-border-subtle bg-surface-muted px-6 py-5">
      <p
        class="mb-1 text-xs font-semibold tracking-wider text-text-primary uppercase">
        {{ contextLabel }}
      </p>
      <h2 class="text-2xl font-bold text-text-primary">{{ analysisName }}</h2>
      <div
        v-if="hasSampleMetadata"
        class="mt-3 flex flex-col gap-1 border-t border-border-subtle pt-3">
        <div
          v-if="sample?.material"
          class="flex items-center gap-1.5 text-xs text-text-secondary">
          <span class="font-medium text-text-primary">{{ $t('W: Materiale') }}:</span>
          <span>{{ sample.material }}</span>
        </div>
        <div
          v-if="sample?.builtYear"
          class="flex items-center gap-1.5 text-xs text-text-secondary">
          <span class="font-medium text-text-primary">{{ $t('W: Opførelsesår') }}:</span>
          <span>{{ sample.builtYear }}</span>
        </div>
        <div
          v-if="sample?.buildingLocation"
          class="flex items-center gap-1.5 text-xs text-text-secondary">
          <span class="font-medium text-text-primary">{{ $t('W: Sted') }}:</span>
          <span>{{ sample.buildingLocation }}</span>
        </div>
        <div
          v-if="sample?.additionalNotes"
          class="flex items-start gap-1.5 text-xs text-text-secondary">
          <span class="shrink-0 font-medium text-text-primary">{{ $t('W: Note') }}:</span>
          <span>{{ sample.additionalNotes }}</span>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div
      v-if="isLoading"
      class="px-6 py-10 text-center text-sm text-text-muted">
      {{ $t('W: Henter resultater...') }}
    </div>

    <!-- Content -->
    <div
      v-else
      class="flex flex-col divide-y divide-border-subtle py-2">
      <div
        v-for="(test, index) in analysisTests"
        :key="test.analysisTestID"
        :class="['px-6 py-5', index % 2 !== 0 ? 'bg-surface-muted' : '']">
        <div class="mb-3 flex items-center justify-between gap-3">
          <!-- min-w-0 so a long test name wraps instead of squashing the
               control beside it, which does not shrink. -->
          <span
            :class="[
              'min-w-0 text-sm font-semibold',
              excludedTestIDs.includes(test.analysisTestID)
                ? 'text-text-muted line-through opacity-60'
                : 'text-text-primary',
            ]">
            {{ getTestName(test.analysisTestID) }}
          </span>

          <!-- Off by default, on = the test is left out — matching every other
               switch in this modal and in analysis setup, where off is the
               quiet normal state and on enables the named thing. -->
          <div
            v-if="togglableTestIDs.includes(test.analysisTestID)"
            class="flex shrink-0 items-center gap-2">
            <span class="text-xs text-text-secondary">
              {{ $t('W: Udelad fra rapport') }}
            </span>
            <AppSwitch
              :isActive="excludedTestIDs.includes(test.analysisTestID)"
              :isDisabled="!canEnterResults || isLastActiveTest(test.analysisTestID)"
              :title="
                isLastActiveTest(test.analysisTestID)
                  ? $t('W: Mindst én test skal udføres')
                  : $t('W: Udelad fra rapport')
              "
              @on-toggle="onToggleTestExcluded(test.analysisTestID)" />
          </div>
        </div>

        <AnalysisResultFields
          :class="
            excludedTestIDs.includes(test.analysisTestID)
              ? 'pointer-events-none opacity-50'
              : ''
          "
          :config="getResultFieldsConfig(test.analysisTestID, test.configSnapshot)"
          :modelValue="resultValues[test.analysisTestID] ?? null"
          @update:model-value="resultValues[test.analysisTestID] = $event"
          :reportLocale="reportLocale"
          :showQaWarning="triggeringTestIDs.includes(test.analysisTestID)"
          :isDisabled="!canEnterResults || excludedTestIDs.includes(test.analysisTestID)"
          :pendingReportDescriptionItems="pendingReportDescriptionItems"
          @on-pending-report-description-item-create="onPendingReportDescriptionItemCreate" />
      </div>

      <!-- Comment -->
      <div class="px-6 py-5">
        <label class="mb-1 block text-xs font-medium text-text-secondary">
          {{ $t('W: Kommentar') }}
        </label>
        <input
          type="text"
          class="w-full rounded-lg border border-border-subtle bg-surface px-3 py-2 text-sm focus:border-action focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          :class="{ 'border-action bg-surface-muted': comment !== '' }"
          :disabled="!canEnterResults"
          :placeholder="$t('W: Tilføj en kommentar til analysen...')"
          v-model="comment" />
      </div>

      <!-- Equipment selector -->
      <div
        v-if="equipmentSelectionEnabled"
        class="px-6 py-5">
        <UiComboboxMultiVee
          name="equipmentIDs"
          :label="$t('W: Udstyr')"
          :options="equipmentOptions"
          :emptyText="$t('W: Intet udstyr konfigureret')"
          :isDisabled="!canEnterResults"
          :closeOnSelect="true"
          v-model="selectedEquipmentIDs" />
      </div>

      <!-- Tillægsydelser (per-analysis add-on services) — batched with the
           main "Gem" save, same as equipment above. -->
      <div
        v-if="addonSelectionVisible"
        class="px-6 py-5">
        <UiComboboxMultiVee
          name="addonServiceIDs"
          :label="$t('W: Tillægsydelser')"
          :options="addonOptions"
          :emptyText="$t('W: Ingen tillægsydelser konfigureret')"
          :isDisabled="!canEnterResults"
          :closeOnSelect="true"
          v-model="selectedAddonIDs" />
      </div>
    </div>

    <!-- Footer -->
    <div class="flex justify-end gap-3 border-t border-border-subtle px-6 py-4">
      <p
        v-if="errorText"
        class="mr-auto self-center text-sm text-feedback-error">
        {{ errorText }}
      </p>
      <UiBaseButton
        v-if="!isCompleted"
        :text="$t('W: Gem')"
        theme="neutral"
        :isDisabled="isLoading || !canEnterResults"
        :isLoading="isSaving"
        @button-click="onSave" />
      <UiBaseButton
        v-if="
          isAnalysisAwaitingQa &&
          !hasUnsavedChanges &&
          !isAwaitingQaByCurrentUser
        "
        :text="$t('W: Godkend QA')"
        :isDisabled="isLoading || !canEnterResults"
        :isLoading="isApprovingQa"
        @button-click="onApproveQaClick" />
      <UiBaseButton
        v-else-if="currentlyTriggersQa"
        :text="$t('W: Anmod om QA')"
        :isDisabled="isLoading || !allTestsFilled || !canEnterResults"
        :isLoading="isRequestingQa"
        @button-click="onRequestQaClick" />
      <UiBaseButton
        v-else
        :text="
          isCompleted ? $t('W: Opdater og f\u00e6rdig') : $t('W: F\u00e6rdig')
        "
        :isDisabled="isLoading || !allTestsFilled || !canEnterResults"
        :isLoading="isCompleting"
        @button-click="onCompleteClick" />
    </div>
  </div>
</template>

<script lang="ts">
import { mapState, mapActions } from 'pinia'
import { useAnalysisTestStore } from '@/stores/analysisTestStore'
import { useAnalysisStore } from '@/stores/analysisStore'
import { useAppStore } from '@/stores/appStore'
import { useTextListStore } from '@/stores/textListStore'
import { useOrderStore } from '@/stores/orderStore'
import { useAnalysisResultStore } from '@/stores/analysisResultStore'
import { useModalStore } from '@/stores/modalStore'
import { useLabEquipmentStore } from '@/stores/labEquipmentStore'
import { useUserStore } from '@/stores/userStore'
import { useAddonServiceStore } from '@/stores/addonServiceStore'
import type { LabEquipment } from '@/globals/typescript/models/LabEquipment'
import { useToast } from '@/globals/typescript/utils/toast'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { getSampleLabel } from '@/globals/typescript/utils/sampleLabel'
import { doesResultTriggerQa } from '@/globals/typescript/utils/doesResultTriggerQa'
import {
  buildResultFieldsConfigFromAnalysisTest,
  type TAnalysisResultFieldsConfig,
} from '@/globals/typescript/utils/analysisResultFieldsConfig'
import type {
  IAnalysisResult,
  TAnalysisResultValue,
} from '@team-milva/lab-models/resources'
import type { IOrderSampleAnalysisTest } from '@team-milva/lab-models/resources'

export default {
  name: 'EnterAnalysisResultsModal',

  emits: ['close'],

  props: {
    orderID: { type: String, required: true },
    sampleID: { type: String, required: true },
    analysisID: { type: String, required: true },
    isCompleted: { type: Boolean, default: false },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      // Captured synchronously, before `created()`'s awaits: registration has to
      // wait for the pre-fill, and by then another modal (the PIN prompt) may be
      // on top of the stack.
      modalEntryID: useModalStore().top?.id,
      resultValues: {} as Record<string, TAnalysisResultValue | null>,
      pendingReportDescriptionItems: [] as {
        id: string
        reportDescriptionID: string
        text: string
        locale: string
      }[],
      existingResultIDs: {} as Record<string, string>,
      // Tests the technician has turned off. Seeded from the order in
      // created() and sent in full on every save — never a delta.
      excludedTestIDs: [] as string[],
      originalExcludedTestIDs: [] as string[],
      selectedEquipmentIDs: [] as string[],
      selectedAddonIDs: [] as string[],
      originalAddonIDs: [] as string[],
      comment: '',
      isLoading: true,
      isSaving: false,
      isCompleting: false,
      isRequestingQa: false,
      isApprovingQa: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useAnalysisTestStore, ['allAnalysisTests']),
    ...mapState(useAnalysisStore, ['allAnalyses']),
    ...mapState(useOrderStore, ['allOrders']),
    ...mapState(useAppStore, ['currentLocale']),
    ...mapState(useTextListStore, ['systemLists']),
    ...mapState(useAnalysisResultStore, ['sampleAnalysisResults']),
    ...mapState(useLabEquipmentStore, ['labEquipment']),
    ...mapState(useUserStore, ['currentUser']),
    ...mapState(useAddonServiceStore, ['allAddonServices']),

    // Prove-based and single-select tests whose currently-entered (not yet
    // necessarily saved) value matches that test's configured QA trigger.
    triggeringTestIDs(): string[] {
      return this.analysisTests
        .filter((test) => test.status !== 'cancelled')
        // A turned-off test needs no result and no QA. It can still be
        // holding a stale value that matches its trigger, so filter on the
        // toggle rather than relying on the value being empty.
        .filter((test) => !this.excludedTestIDs.includes(test.analysisTestID))
        .filter((test) => {
          // Already resolved by a prior approval and untouched since — don't
          // re-flag it as needing a fresh QA look just for still matching
          // the trigger pattern (e.g. reopening an approved "not-proved"
          // result without editing anything).
          //
          // `?? null` is load-bearing: an order created before MIL-481 has no
          // `qaApprovedAt` key at all on a test nobody has requested QA for.
          // `undefined !== null` is true, so without it every such test counted
          // as "approved" the moment its result was saved — the request aborted
          // with "Ændring gemt" and the technician was left on a "Færdig" button.
          if (
            (test.timestamps.qaApprovedAt ?? null) !== null &&
            !this.hasTestValueChanged(test.analysisTestID)
          ) {
            return false
          }
          return true
        })
        .map((test) => test.analysisTestID)
        .filter((testID) => {
          const analysisTest = this.allAnalysisTests.find(
            (x) => x.id === testID
          )
          return doesResultTriggerQa(analysisTest, this.resultValues[testID])
        })
    },

    isAnalysisAwaitingQa(): boolean {
      return this.analysisTests.some((test) => test.status === 'awaiting-qa')
    },

    isAwaitingQaByCurrentUser(): boolean {
      const awaitingTest = this.analysisTests.find(
        (test) => test.status === 'awaiting-qa'
      )
      return (
        !!awaitingTest &&
        awaitingTest.qaRequestedByUserID === (this.currentUser?.id ?? null)
      )
    },

    // Whether the currently-entered (possibly unsaved) values require QA
    // right now — live/reactive, independent of what's actually stored, so
    // the button reacts the same way the inline "QA påkrævet" badge does.
    currentlyTriggersQa(): boolean {
      return this.triggeringTestIDs.length > 0
    },

    // Whether any currently-entered value differs from what's actually
    // stored — mirrors saveAnalysisResultsService's own change detection, so
    // approving while awaiting QA can save-and-cancel instead of approving
    // stale data, without a separate manual "Gem" step being required first.
    hasUnsavedChanges(): boolean {
      if (this.hasExclusionChanges) return true
      return this.analysisTests.some(
        (test) =>
          test.status !== 'cancelled' &&
          this.hasTestValueChanged(test.analysisTestID)
      )
    },

    // Turning a test on or off is an unsaved change in its own right, even
    // when no result value was touched — and the backend treats it the same
    // way, discarding a pending QA request on save.
    hasExclusionChanges(): boolean {
      if (this.excludedTestIDs.length !== this.originalExcludedTestIDs.length) {
        return true
      }
      return this.excludedTestIDs.some(
        (testID) => !this.originalExcludedTestIDs.includes(testID)
      )
    },

    // A test the technician may turn off: the order's own frozen snapshot says
    // it is optional, or it is already off. The second half matters — without
    // it, revoking `isOptional` in the catalog would strand an already-excluded
    // test with no way to turn it back on, silently dropping it from the report
    // forever. Also covers orders predating the snapshot, which have neither.
    togglableTestIDs(): string[] {
      return this.analysisTests
        .filter(
          (test) =>
            test.status !== 'cancelled' &&
            ((test.options?.isOptional ?? false) ||
              this.excludedTestIDs.includes(test.analysisTestID))
        )
        .map((test) => test.analysisTestID)
    },

    // At least one test must stay on. Enforced server-side too — this only
    // keeps the last remaining switch from offering a click that would fail.
    isLastActiveTest(): (testID: string) => boolean {
      return (testID: string) => {
        if (this.excludedTestIDs.includes(testID)) return false
        const activeTests = this.analysisTests.filter(
          (test) =>
            test.status !== 'cancelled' &&
            !this.excludedTestIDs.includes(test.analysisTestID)
        )
        return activeTests.length <= 1
      }
    },

    equipmentSelectionEnabled(): boolean {
      return (
        this.allAnalyses.find((x) => x.id === this.analysisID)?.options
          ?.equipmentSelectionEnabled ?? false
      )
    },

    // Covers save/complete/request-QA/approve-QA, equipment selection, and
    // the per-analysis comment. This modal is only reachable via already
    // gated menu entry points — kept here too as defense in depth.
    canEnterResults(): boolean {
      return this.canUser('orders.enterResults')
    },

    // Active equipment plus anything already recorded on this analysis, so a
    // piece of equipment deactivated after the fact stays visible on the
    // records that reference it instead of silently vanishing from them.
    // Deselecting it drops it from the list for good.
    selectableEquipment(): LabEquipment[] {
      return (this.labEquipment as LabEquipment[]).filter(
        (x) =>
          x.activeState === 'active' ||
          this.selectedEquipmentIDs.includes(x.id)
      )
    },

    // Serial number as the option's second line — a lab can hold several
    // devices sharing a name, and the serial number is the only thing telling
    // them apart. The combobox matches it when searching too.
    equipmentOptions(): { value: string; text: string; subtitle?: string }[] {
      return this.selectableEquipment
        .map((equipment) => ({
          value: equipment.id,
          text: equipment.getName(this.currentLocale),
          ...(equipment.serialNumber
            ? { subtitle: equipment.serialNumber }
            : {}),
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    applicableAddonServices() {
      return this.allAddonServices.filter(
        (addonService) =>
          addonService.activeState === 'active' &&
          addonService.scope === 'per-analysis' &&
          addonService.applicableAnalysisIDs.includes(this.analysisID)
      )
    },

    addonOptions(): { value: string; text: string }[] {
      return this.applicableAddonServices
        .map((addonService) => ({
          value: addonService.id,
          text: addonService.getName(this.currentLocale),
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    // Shown whenever there's something to pick, or something already attached
    // to remove — covers an addon service later reconfigured away from this
    // analysis while still attached to an existing order.
    addonSelectionVisible(): boolean {
      return this.addonOptions.length > 0 || this.selectedAddonIDs.length > 0
    },

    allTestsFilled(): boolean {
      if (this.isLoading) return false
      return this.analysisTests.every((test) => {
        const testID = test.analysisTestID
        // A turned-off test needs no result — without this, "Færdig" could
        // never be pressed on an analysis with one.
        if (this.excludedTestIDs.includes(testID)) return true
        if (this.existingResultIDs[testID]) return this.hasResultForTest(testID)
        const type = this.getTestType(testID)
        if (
          ![
            'prove-based',
            'value-based',
            'single-select',
            'multi-select-text',
            'multi-select-number',
          ].includes(type)
        )
          return true
        return this.hasResultForTest(testID)
      })
    },

    order() {
      return this.allOrders.find((x) => x.id === this.orderID) ?? null
    },

    sample() {
      return (
        this.order?.samples.find((x) => x.sampleID === this.sampleID) ?? null
      )
    },

    reportLocale(): string {
      return this.order?.reportLocale ?? this.currentLocale
    },

    contextLabel(): string {
      if (!this.sample) return ''
      const label = getSampleLabel(this.sample.sampleNumber, this.currentLocale)
      const id = this.sample.label?.id
      return id ? `${label} - ${id}` : label
    },

    hasSampleMetadata(): boolean {
      return !!(
        this.sample?.material ||
        this.sample?.builtYear ||
        this.sample?.buildingLocation ||
        this.sample?.additionalNotes
      )
    },

    analysisName(): string {
      const analysis = this.allAnalyses.find((x) => x.id === this.analysisID)
      return analysis ? analysis.getName(this.currentLocale) : ''
    },

    analysisTests(): IOrderSampleAnalysisTest[] {
      const sampleAnalysis = this.sample?.analyses.find(
        (x) => x.analysisID === this.analysisID
      )
      return sampleAnalysis?.analysisTests ?? []
    },
  },

  watch: {
    sampleAnalysisResults: {
      handler(results: IAnalysisResult[]) {
        if (this.isLoading) return
        // Deliberately does NOT re-baseline the guard afterwards, unlike the
        // record-prop watchers on the edit panes. `syncFromResults` only writes
        // the tests the store has a result for, so a colleague's save leaves an
        // in-progress entry on an untouched test intact — re-snapshotting here
        // would fold that entry into the baseline and let it be discarded
        // silently. Reading dirty after someone else's write is the safe error.
        this.syncFromResults(results)
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions(useAnalysisResultStore, [
      'getAnalysisResultsForSample',
      'saveAnalysisResults',
      'completeAnalysisResult',
      'requestAnalysisQa',
      'approveAnalysisQa',
    ]),
    ...mapActions(useModalStore, ['open', 'closeTop']),
    ...mapActions(useLabEquipmentStore, ['getlabEquipment']),
    ...mapActions(useOrderStore, [
      'updateAnalysisComment',
      'addAnalysisAddonLine',
      'removeAnalysisAddonLine',
    ]),
    ...mapActions(useUserStore, ['canUser']),

    syncFromResults(results: IAnalysisResult[]): void {
      for (const result of results) {
        if (result.analysisID !== this.analysisID) continue
        this.existingResultIDs[result.analysisTestID] = result.id
        this.resultValues[result.analysisTestID] = result.result
      }
    },

    getTestName(analysisTestID: string): string {
      const test = this.allAnalysisTests.find((x) => x.id === analysisTestID)
      return test ? test.getName(this.currentLocale) : analysisTestID
    },

    getTestType(analysisTestID: string): string {
      return (
        this.allAnalysisTests.find((x) => x.id === analysisTestID)?.type ?? ''
      )
    },

    getResultFieldsConfig(
      analysisTestID: string,
      configSnapshot: IOrderSampleAnalysisTest['configSnapshot']
    ): TAnalysisResultFieldsConfig {
      const test = this.allAnalysisTests.find((x) => x.id === analysisTestID)
      // Result entry is in-app UI, so labels resolve at the technician's own
      // locale — not the order's report locale.
      const context = {
        locale: this.currentLocale,
        resultTexts: this.systemLists.find((l) => l.systemKey === 'result-texts'),
      }
      return test
        ? buildResultFieldsConfigFromAnalysisTest(test, context, configSnapshot)
        : {
            type: 'prove-based',
            provedLabel: this.$t('term.yes'),
            notProvedLabel: this.$t('term.no'),
          }
    },

    getMultiSelectTextAnswerMode(
      analysisTestID: string
    ): 'none' | 'optional' | 'required' {
      const test = this.allAnalysisTests.find((x) => x.id === analysisTestID)
      return test?.type === 'multi-select-text'
        ? (test.options.answerMode ?? 'optional')
        : 'optional'
    },

    onPendingReportDescriptionItemCreate(payload: {
      id: string
      reportDescriptionID: string
      text: string
      locale: string
    }): void {
      this.pendingReportDescriptionItems.push(payload)
    },

    // Whether the currently-entered value for this test differs from what's
    // actually stored — mirrors saveAnalysisResultsService's own comparison.
    hasTestValueChanged(analysisTestID: string): boolean {
      const stored = this.sampleAnalysisResults.find(
        (r) =>
          r.analysisID === this.analysisID &&
          r.analysisTestID === analysisTestID
      )
      if (!stored) return this.hasResultForTest(analysisTestID)
      return (
        JSON.stringify(stored.result) !==
        JSON.stringify(this.buildResultValue(analysisTestID))
      )
    },

    hasResultForTest(analysisTestID: string): boolean {
      const value = this.resultValues[analysisTestID]
      if (!value) return false
      if (value.type === 'prove-based') return value.isProved !== null
      if (value.type === 'value-based')
        return value.value !== null || value.specialValue !== null
      if (value.type === 'single-select') return value.answerID !== null
      if (value.type === 'multi-select-text') {
        if (value.nothingToAdd) return true
        if (value.items.length === 0) return false
        const answerMode = this.getMultiSelectTextAnswerMode(analysisTestID)
        if (answerMode === 'required')
          return value.items.every((item) => item.answerID != null)
        return true
      }
      if (value.type === 'multi-select-number') {
        if (value.nothingToAdd) return true
        return (
          value.items.length > 0 &&
          value.items.every(
            (item) => item.value != null || item.answerOverrideID != null
          )
        )
      }
      return false
    },

    buildResultValue(analysisTestID: string): TAnalysisResultValue | null {
      return this.resultValues[analysisTestID] ?? null
    },

    async _saveComment(): Promise<void> {
      await this.updateAnalysisComment(
        this.orderID,
        this.sampleID,
        this.analysisID,
        this.comment
      )
    },

    async onSave(): Promise<void> {
      this.isSaving = true
      this.errorText = ''
      try {
        await this._saveAllResults()
        await this._saveComment()
        this.unsavedGuard.takeSnapshot()
        useToast().success(this.$t('W: Analyseresultater gemt'))
        this.closeTop()
      } catch {
        this.errorText = this.$t('W: Der opstod en fejl')
        useToast().error(this.$t('W: Kunne ikke gemme analyseresultater'))
      } finally {
        this.isSaving = false
      }
    },

    // Builds the batch payload synchronously and sends a single request.
    onToggleTestExcluded(testID: string): void {
      if (this.excludedTestIDs.includes(testID)) {
        this.excludedTestIDs = this.excludedTestIDs.filter((x) => x !== testID)
        return
      }
      if (this.isLastActiveTest(testID)) return
      this.excludedTestIDs = [...this.excludedTestIDs, testID]
    },

    async _saveAllResults(): Promise<void> {
      const results = this.analysisTests
        .map((test) => {
          const testID = test.analysisTestID
          const existingID = this.existingResultIDs[testID]
          const hasResult = this.hasResultForTest(testID)
          const resultValue = this.buildResultValue(testID)
          if (!existingID && !hasResult) return null
          if (!resultValue) return null
          return {
            resultID: existingID || undefined,
            analysisTestID: testID,
            result: resultValue,
          }
        })
        .filter((x): x is NonNullable<typeof x> => x !== null)

      const addedAddonIDs = this.selectedAddonIDs.filter(
        (id) => !this.originalAddonIDs.includes(id)
      )
      const removedAddonIDs = this.originalAddonIDs.filter(
        (id) => !this.selectedAddonIDs.includes(id)
      )

      if (
        results.length === 0 &&
        !this.equipmentSelectionEnabled &&
        this.pendingReportDescriptionItems.length === 0 &&
        addedAddonIDs.length === 0 &&
        removedAddonIDs.length === 0 &&
        // A save that only turns a test on or off has no results to send, but
        // still has to reach the backend.
        !this.hasExclusionChanges
      )
        return

      const createdIDs = await this.saveAnalysisResults({
        orderID: this.orderID,
        sampleID: this.sampleID,
        analysisID: this.analysisID,
        results,
        equipmentIDs: this.selectedEquipmentIDs,
        newReportDescriptionItems:
          this.pendingReportDescriptionItems.length > 0
            ? this.pendingReportDescriptionItems
            : undefined,
        excludedAnalysisTestIDs: this.excludedTestIDs,
      })

      this.originalExcludedTestIDs = [...this.excludedTestIDs]

      // Items are now persisted — clear so getMultiSelectText/NumberAllItems()
      // falls back to the (now updated) Collection item store instead of local state
      this.pendingReportDescriptionItems = []

      // Record newly created IDs so completeAnalysisResult can reference them
      for (const [analysisTestID, resultID] of Object.entries(createdIDs)) {
        this.existingResultIDs[analysisTestID] = resultID
      }

      for (const addonID of addedAddonIDs) {
        await this.addAnalysisAddonLine(this.orderID, this.analysisID, addonID)
      }
      for (const addonID of removedAddonIDs) {
        await this.removeAnalysisAddonLine(
          this.orderID,
          this.analysisID,
          addonID
        )
      }
      this.originalAddonIDs = [...this.selectedAddonIDs]
    },

    onCompleteClick(): void {
      this.open({
        name: 'EnterPinModal',
        width: 'sm',
        props: {
          onConfirm: () => this.onComplete(),
        },
      })
    },

    async onComplete(): Promise<void> {
      this.isCompleting = true
      this.errorText = ''
      try {
        await this._saveAllResults()
        await this._saveComment()
        this.unsavedGuard.takeSnapshot()

        // Mark all results as completed
        for (const test of this.analysisTests) {
          const resultID = this.existingResultIDs[test.analysisTestID]
          if (!resultID) continue
          await this.completeAnalysisResult(
            resultID,
            this.orderID,
            this.sampleID
          )
        }

        useToast().success(this.$t('W: Analyseresultater fuldført'))
        // Close EnterPinModal (currently on top), then close this modal (re-mounted underneath)
        this.closeTop()
        this.closeTop()
      } catch {
        this.errorText = this.$t('W: Der opstod en fejl')
        useToast().error(this.$t('W: Kunne ikke fuldføre analyseresultater'))
      } finally {
        this.isCompleting = false
      }
    },

    onRequestQaClick(): void {
      this.open({
        name: 'EnterPinModal',
        width: 'sm',
        props: {
          onConfirm: () => this.onRequestQa(),
        },
      })
    },

    async onRequestQa(): Promise<void> {
      this.isRequestingQa = true
      this.errorText = ''
      try {
        await this._saveAllResults()
        await this._saveComment()
        // Load-bearing here, not just tidiness: the early return below leaves
        // this modal open after a successful save, so without it the guard
        // would warn about work that is already stored.
        this.unsavedGuard.takeSnapshot()

        // An edit just saved above may have changed the value away from
        // what triggers QA at all (e.g. the original requester correcting
        // their own entry) — nothing left to request in that case. Let the
        // button recompute on its own rather than forcing a request call
        // that the backend would reject anyway.
        if (this.triggeringTestIDs.length === 0) {
          useToast().success(this.$t('W: Ændring gemt'))
          return
        }

        await this.requestAnalysisQa(
          this.orderID,
          this.sampleID,
          this.analysisID
        )

        useToast().success(this.$t('W: QA-godkendelse anmodet'))
        this.closeTop()
        this.closeTop()
      } catch {
        this.errorText = this.$t('W: Der opstod en fejl')
        useToast().error(this.$t('W: Kunne ikke anmode om QA-godkendelse'))
      } finally {
        this.isRequestingQa = false
      }
    },

    onApproveQaClick(): void {
      this.open({
        name: 'EnterPinModal',
        width: 'sm',
        props: {
          onConfirm: () => this.onApproveQa(),
        },
      })
    },

    async onApproveQa(): Promise<void> {
      this.isApprovingQa = true
      this.errorText = ''
      try {
        await this.approveAnalysisQa(
          this.orderID,
          this.sampleID,
          this.analysisID
        )

        useToast().success(this.$t('W: QA godkendt'))
        this.closeTop()
        this.closeTop()
      } catch {
        this.errorText = this.$t('W: Der opstod en fejl')
        useToast().error(this.$t('W: Kunne ikke godkende QA'))
      } finally {
        this.isApprovingQa = false
      }
    },

  },

  async created() {
    await this.getAnalysisResultsForSample(this.sampleID)
    this.syncFromResults(this.sampleAnalysisResults)
    this.isLoading = false

    const sampleAnalysis = this.sample?.analyses.find(
      (x) => x.analysisID === this.analysisID
    )
    this.selectedEquipmentIDs = [...(sampleAnalysis?.equipmentIDs ?? [])]
    this.comment = sampleAnalysis?.comment ?? ''

    const initialExcludedTestIDs = (sampleAnalysis?.analysisTests ?? [])
      .filter((test) => test.options?.isExcludedFromReport ?? false)
      .map((test) => test.analysisTestID)
    this.excludedTestIDs = [...initialExcludedTestIDs]
    this.originalExcludedTestIDs = [...initialExcludedTestIDs]

    const initialAddonIDs = (this.order?.orderLines ?? [])
      .filter(
        (line) => line.type === 'addon' && line.analysisID === this.analysisID
      )
      .map((line) => line.addonID ?? '')
    this.selectedAddonIDs = [...initialAddonIDs]
    this.originalAddonIDs = [...initialAddonIDs]

    if (this.equipmentSelectionEnabled) {
      this.getlabEquipment()
    }

    // Last in created(), after every field above has been seeded — `register()`
    // snapshots immediately, so registering before the pre-fill would make the
    // modal open dirty and warn on a close the technician never edited.
    this.unsavedGuard.register({
      snapshot: () => ({
        resultValues: this.resultValues,
        pendingReportDescriptionItems: this.pendingReportDescriptionItems,
        selectedEquipmentIDs: this.selectedEquipmentIDs,
        selectedAddonIDs: this.selectedAddonIDs,
        excludedTestIDs: this.excludedTestIDs,
        comment: this.comment,
      }),
      modalId: this.modalEntryID,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
